import { Button, FormField, Modal, Textarea, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pause, Play } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  getLicenseDetailQueryKey,
  getLicenseStatusQueryKey,
  type LicenseResponse,
  resumePlatformMutation,
  suspendPlatformMutation,
} from "@/api";
import { DangerZone } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";

type Pending = "suspend" | "resume" | null;

function ConfirmBody({ action }: Readonly<{ action: "suspend" | "resume" }>) {
  const { t } = useTranslation("adminLicense");
  if (action === "resume") {
    return (
      <Text variant="body-sm" tone="secondary">
        {t("killSwitch.resumeBody")}
      </Text>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <Text variant="body-sm" tone="secondary">
        {t("killSwitch.suspendIntro")}
      </Text>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {(["consequenceLockout", "consequenceNotice", "consequenceUntil"] as const).map((key) => (
          <li key={key}>
            <Text as="span" variant="body-sm" tone="secondary">
              {t(`killSwitch.${key}`)}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Install admins: suspend (or resume) the whole platform, always confirm-first. */
export function KillSwitch({ license }: Readonly<{ license: LicenseResponse }>) {
  const { t } = useTranslation("adminLicense");
  const toast = useToast();
  const queryClient = useQueryClient();
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState<Pending>(null);
  const suspend = useMutation(suspendPlatformMutation());
  const resume = useMutation(resumePlatformMutation());
  const suspended = !license.isActive;

  const done = (message: string) => ({
    onSuccess: (res: { data: LicenseResponse }) => {
      queryClient.setQueryData(getLicenseDetailQueryKey(), res);
      void queryClient.invalidateQueries({ queryKey: getLicenseStatusQueryKey() });
      toast.success(message);
      setPending(null);
      setReason("");
    },
    onError: (error: unknown) => toast.error(errorMessage(error, t("killSwitch.failed"))),
  });

  const confirm = () => {
    if (pending === "suspend") {
      suspend.mutate({ body: { reason: reason.trim() || null } }, done(t("killSwitch.suspended")));
    } else if (pending === "resume") {
      resume.mutate({ body: {} }, done(t("killSwitch.resumed")));
    }
  };

  let dialogTitle: string | undefined;
  if (pending === "suspend") dialogTitle = t("killSwitch.suspendTitle");
  else if (pending === "resume") dialogTitle = t("killSwitch.resumeTitle");

  return (
    <DangerZone title={t("killSwitch.title")} description={t("killSwitch.description")}>
      {suspended ? (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {license.suspendedAt && (
            <Text variant="body-sm" tone="secondary">
              {t("killSwitch.suspendedSince", { date: formatDateTime(license.suspendedAt) })}
            </Text>
          )}
          <Button variant="primary" leadingIcon={<Play size={16} />} onClick={() => setPending("resume")}>
            {t("killSwitch.resume")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <FormField label={t("killSwitch.reason")} htmlFor={reasonId}>
            <Textarea
              id={reasonId}
              rows={2}
              value={reason}
              placeholder={t("killSwitch.reasonPlaceholder")}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormField>
          <div>
            <Button variant="danger" leadingIcon={<Pause size={16} />} onClick={() => setPending("suspend")}>
              {t("killSwitch.suspend")}
            </Button>
          </div>
        </div>
      )}
      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title={dialogTitle}
        actions={
          <>
            <Button variant="secondary" onClick={() => setPending(null)}>
              {t("killSwitch.cancel")}
            </Button>
            <Button
              variant={pending === "resume" ? "primary" : "danger"}
              loading={suspend.isPending || resume.isPending}
              onClick={confirm}
            >
              {pending === "resume" ? t("killSwitch.resume") : t("killSwitch.suspend")}
            </Button>
          </>
        }
      >
        {pending && <ConfirmBody action={pending} />}
      </Modal>
    </DangerZone>
  );
}
